import React from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { get } from 'lodash-es';
import classNames from 'classnames';
import { InventoryEntities } from 'client/data/models/inventory';
import { ColorSwatch } from 'site-modules/shared/components/color-swatch/color-swatch';
import { SUMMARY_CONFIG_VDP } from 'site-modules/shared/constants/inventory/vehicle-summary-config';
import { getBedLengthValue } from 'site-modules/shared/utils/features-format';
import { VehicleSummaryMpgContent } from 'site-modules/shared/components/inventory/vehicle-summary-mpg-content/vehicle-summary-mpg-content';
import { VdpPopoverItem } from 'site-modules/shared/components/inventory/vdp-popover-item/vdp-popover-item';

import './vehicle-summary-item.scss';

export function VehicleSummaryItem({ vinData = {}, name, value = null, summaryDisplayConfig = SUMMARY_CONFIG_VDP }) {
  const vehicleInfo = get(vinData, 'vehicleInfo');
  const partsInfo = get(vehicleInfo, 'partsInfo');
  const styleInfo = get(vehicleInfo, 'styleInfo');
  const vehicleColors = get(vehicleInfo, 'vehicleColors');
  const bedLengthMeasurement = get(partsInfo, 'bedLengthMeasurement');
  const exteriorColor = get(vehicleColors, 'exterior', {});
  const interiorColor = get(vehicleColors, 'interior', {});
  const exteriorColorName = get(exteriorColor, 'name', '');
  const interiorColorName = get(interiorColor, 'name', '');
  const model = get(styleInfo, 'model', '');

  let display;
  let tooltip;
  let valueClass = '';
  const config = summaryDisplayConfig[name];

  switch (name) {
    case 'exteriorColor':
      display = (
        <div>
          <ColorSwatch
            id="vin_exterior_color_swatch"
            classNames="d-inline-block bg-gray-lighter align-text-top"
            color={exteriorColor}
            displayColorName
            title={exteriorColorName}
          />
        </div>
      );
      valueClass = 'summary-value';
      break;
    case 'interiorColor':
      display = (
        <div>
          <ColorSwatch
            id="vin_interior_color_swatch"
            classNames="d-inline-block bg-gray-lighter align-text-top"
            color={interiorColor}
            displayColorName
            title={interiorColorName}
          />
        </div>
      );
      valueClass = 'summary-value';
      break;
    case 'mpg':
      display = (
        <VehicleSummaryMpgContent
          summaryItemName={config.title}
          citySectionTooltipText={`Based on model year EPA mileage ratings for a standardly equipped ${model}. Use for comparison purposes only. Your mileage will vary depending on how you drive and maintain your vehicle, driving conditions, battery-pack age/condition and other factors.`}
          cityMPG={get(styleInfo, 'fuel.epaCityMPG')}
          combinedMPG={get(styleInfo, 'fuel.epaCombinedMPG')}
          highwayMPG={get(styleInfo, 'fuel.epaHighwayMPG')}
        />
      );
      break;
    case 'mpge':
      display = (
        <VehicleSummaryMpgContent
          summaryItemName={config.title}
          citySectionTooltipText="MPGe is the official metric that the EPA uses to measure the efficiency of alternative-fuel (including electric) vehicles. Just like regular MPG shows how far a car will travel on one gallon of gas, MPGe shows how far a vehicle will drive on 33.7 kWh of electricity — the energy equivalent of one gallon of gasoline."
          combinedSectionTooltipText="A combined total of 45% city MPGe + 55% highway MPGe."
          cityMPG={get(styleInfo, 'fuel.epaElectricCityMPG')}
          combinedMPG={get(styleInfo, 'fuel.epaElectricCombinedMPG')}
          highwayMPG={get(styleInfo, 'fuel.epaElectricHighwayMPG')}
        />
      );
      break;
    case 'bedLengthMeasurement':
      tooltip = get(config, 'tooltip', '');
      display = getBedLengthValue(bedLengthMeasurement);
      break;
    default:
      tooltip = get(config, 'tooltip', '');
      display = get(config, 'display', '').replace('{value}', value);
      valueClass = get(config, 'valueClass', '');
  }
  const className = get(config, 'className', '');
  const colClassName = get(config, 'colClassName', 'ms-1');

  return (
    <Row
      tag="li"
      className={classNames('vehicle-summary-item d-flex justify-content-between m-0 pb-1 mb-1', className)}
    >
      <Col xs="auto" className="ps-0">
        {get(config, 'title', '')}
      </Col>
      <Col className={classNames('pe-0 fw-bold text-end', colClassName, valueClass)}>
        {display}
        {tooltip && (
          <VdpPopoverItem
            tooltip={tooltip}
            placement="bottom"
            className="text-gray-darker tooltip-sup-override"
            iconClassName="px-0_25 pt-0_5 pb-0 p-md-0"
            screenReaderTooltipLabel={name}
          />
        )}
      </Col>
    </Row>
  );
}

VehicleSummaryItem.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  summaryDisplayConfig: PropTypes.shape({}),
};
